import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { SidebarService } from './sidebar';
import { Auth } from '../auth/auth';
import {
  MySidebarService,
  SidebarEffectiveSection,
  SidebarSavedSection,
  SidebarSectionInput,
  SidebarAvailableMenu,
} from './my-sidebar.service';

export interface SidebarQuickLink {
  id: string;
  name: string;
  link?: string;
  icon?: string;
  enabled: boolean;
  order: number;
  menu_id?: number | null;
  menu_level?: string | null;
  item_type?: 'MENU' | 'QUICK_LINK';
}

export interface SidebarSection {
  id: string;
  name: string;
  enabled: boolean;
  order: number;
  items: SidebarQuickLink[];
}

export interface CustomizedSidebarConfig {
  sections: SidebarSection[];
}

export const DEFAULT_CUSTOMIZED_SIDEBAR_CONFIG: CustomizedSidebarConfig = {
  sections: [],
};

@Injectable({
  providedIn: 'root',
})
export class CustomizedSidebarService {
  private readonly sidebarService = inject(SidebarService);
  private readonly mySidebarService = inject(MySidebarService);
  private readonly auth = inject(Auth);
  private readonly STORAGE_KEY_PREFIX = 'customized_sidebar_config_';

  private readonly configSubject = new BehaviorSubject<CustomizedSidebarConfig>(this.loadStoredConfig());
  readonly config$: Observable<CustomizedSidebarConfig> = this.configSubject.asObservable();

  private cachedAvailableMenus: SidebarAvailableMenu[] = [];
  private inFlightRefresh$: Observable<CustomizedSidebarConfig> | null = null;

  constructor() {
    this.auth.user.subscribe((authUser) => {
      if (this.isRoleSupported(authUser)) {
        this.refreshEffective().subscribe();
      } else {
        this.cachedAvailableMenus = [];
        this.configSubject.next(DEFAULT_CUSTOMIZED_SIDEBAR_CONFIG);
      }
    });
  }

  /** Checks if current user role is eligible for sidebar customization */
  isRoleSupported(user: any): boolean {
    const roleType = (user?.role_type || '').toLowerCase();
    return roleType === 'employee' || roleType === 'super_user';
  }

  /** Refreshes effective custom sidebar from backend API */
  refreshEffective(): Observable<CustomizedSidebarConfig> {
    const user = this.auth.currentUser();
    if (!this.isRoleSupported(user)) {
      return of(this.configSubject.getValue());
    }

    if (this.inFlightRefresh$) {
      return this.inFlightRefresh$;
    }

    this.inFlightRefresh$ = this.mySidebarService.getEffective(false).pipe(
      map((res) => this.mapEffectiveToConfig(res?.data?.sections || [])),
      tap((config) => {
        this.configSubject.next(config);
        this.saveStoredConfig(config);
      }),
      catchError((err) => {
        console.warn('[CustomizedSidebarService] Fallback to cached config on load error:', err);
        return of(this.configSubject.getValue());
      }),
      finalize(() => {
        this.inFlightRefresh$ = null;
      }),
      shareReplay(1),
    );

    return this.inFlightRefresh$;
  }

  /** Fetches available menus from backend API (filtered by tenant allocation + user group) */
  fetchAvailableMenus(): Observable<SidebarAvailableMenu[]> {
    const user = this.auth.currentUser();
    if (!this.isRoleSupported(user)) {
      return of([]);
    }

    return this.mySidebarService.getAvailableMenus().pipe(
      map((res) => res?.data?.menus || []),
      tap((menus) => {
        this.cachedAvailableMenus = menus;
      }),
      catchError((err) => {
        console.error('[CustomizedSidebarService] Error fetching available menus:', err);
        return of([]);
      }),
    );
  }

  /** Dynamically returns available system menus */
  getAvailableSystemMenus(): { id: string; name: string; link: string; icon: string; menu_level?: string }[] {
    if (this.cachedAvailableMenus.length > 0) {
      return this.cachedAvailableMenus.map((m) => ({
        id: String(m.menu_id),
        name: m.menu_name,
        link: m.navigation_url || '',
        icon: m.icon || 'hgi hgi-stroke hgi-rounded hgi-link-01',
        menu_level: m.menu_level,
      }));
    }
    // Fallback to local sidebar menu list if API hasn't loaded yet
    const allMenus = this.sidebarService.getMenuList() || [];
    return allMenus
      .filter((m: any) => m.can_view !== false && m.id !== 99999 && m.link && m.link.trim() !== '')
      .map((m: any) => ({
        id: String(m.id),
        name: m.name,
        link: m.link,
        icon: m.icon || 'hgi hgi-stroke hgi-rounded hgi-link-01',
      }));
  }

  /** Gets current configuration */
  getConfig(): CustomizedSidebarConfig {
    return this.configSubject.getValue();
  }

  /** Loads saved arrangement from server (including hidden rows) for the customize editor */
  loadSavedArrangement(): Observable<CustomizedSidebarConfig> {
    const user = this.auth.currentUser();
    if (!this.isRoleSupported(user)) {
      return of(this.loadStoredConfig());
    }

    return this.mySidebarService.getSaved().pipe(
      map((res) => this.mapSavedToConfig(res?.data?.sections || [])),
      catchError((err) => {
        console.error('[CustomizedSidebarService] Error loading saved arrangement:', err);
        return of(this.loadStoredConfig());
      }),
    );
  }

  /** Saves updated configuration to server and updates local state */
  saveConfigToServer(config: CustomizedSidebarConfig): Observable<any> {
    const payload: SidebarSectionInput[] = (config.sections || []).map((sec, secIdx) => ({
      section_name: sec.name.trim(),
      section_display_order: sec.order || secIdx + 1,
      sidebar_display: sec.enabled !== false ? 'Shown' : 'Hidden',
      items: (sec.items || []).map((item, itemIdx) => ({
        item_type: (item.item_type || 'MENU'),
        item_display_order: item.order || itemIdx + 1,
        sidebar_display: item.enabled !== false ? 'Shown' : 'Hidden',
        menu_id: item.menu_id ? Number(item.menu_id) : (Number(item.id) || null),
        menu_level: (item.menu_level as any) || null,
        quick_link_name: item.item_type === 'QUICK_LINK' ? item.name : null,
        quick_link_url: item.item_type === 'QUICK_LINK' ? (item.link || null) : null,
      })),
    }));

    return this.mySidebarService.save(payload).pipe(
      tap(() => {
        this.configSubject.next(config);
        this.saveStoredConfig(config);
        this.refreshEffective().subscribe();
      }),
    );
  }

  /** Resets customization on server and defaults local state */
  resetServerConfig(): Observable<any> {
    return this.mySidebarService.reset().pipe(
      tap(() => {
        this.configSubject.next(DEFAULT_CUSTOMIZED_SIDEBAR_CONFIG);
        this.clearStoredConfig();
      }),
    );
  }

  // --- Internal Mappers ---
  private mapEffectiveToConfig(sections: SidebarEffectiveSection[]): CustomizedSidebarConfig {
    const mappedSections: SidebarSection[] = sections
      .filter((sec) => sec.sidebar_display === 'Shown')
      .map((sec) => ({
        id: String(sec.id),
        name: sec.section_name,
        enabled: sec.sidebar_display === 'Shown',
        order: sec.section_display_order,
        items: (sec.items || [])
          .filter((item) => item.is_available && item.sidebar_display === 'Shown')
          .map((item) => ({
            id: String(item.id || item.menu_id),
            name: item.menu_name || item.quick_link_name || '',
            link: item.navigation_url || item.quick_link_url || '',
            icon: 'hgi hgi-stroke hgi-rounded hgi-link-01',
            enabled: item.sidebar_display === 'Shown',
            order: item.item_display_order,
            menu_id: item.menu_id,
            menu_level: item.current_menu_level || item.menu_level,
            item_type: item.item_type,
          })),
      }));

    return { sections: mappedSections };
  }

  private mapSavedToConfig(sections: SidebarSavedSection[]): CustomizedSidebarConfig {
    const mappedSections: SidebarSection[] = sections.map((sec) => ({
      id: String(sec.id),
      name: sec.section_name,
      enabled: sec.sidebar_display === 'Shown',
      order: sec.section_display_order,
      items: (sec.items || []).map((item) => {
        const menuDef = this.cachedAvailableMenus.find((m) => m.menu_id === item.menu_id);
        return {
          id: String(item.id || item.menu_id),
          name: menuDef?.menu_name || item.quick_link_name || (item.menu_id ? `Menu #${item.menu_id}` : 'Quick Link'),
          link: menuDef?.navigation_url || item.quick_link_url || '',
          icon: menuDef?.icon || 'hgi hgi-stroke hgi-rounded hgi-link-01',
          enabled: item.sidebar_display === 'Shown',
          order: item.item_display_order,
          menu_id: item.menu_id,
          menu_level: item.menu_level || menuDef?.menu_level || 'Primary',
          item_type: item.item_type,
        };
      }),
    }));

    return { sections: mappedSections };
  }

  private getStorageKey(userId?: string | number): string {
    const id = userId || 'default';
    return `${this.STORAGE_KEY_PREFIX}${id}`;
  }

  private loadStoredConfig(): CustomizedSidebarConfig {
    try {
      const storedUser = localStorage.getItem('user');
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const userId = userData?.id || userData?.userID || 'default';
      const raw = localStorage.getItem(this.getStorageKey(userId));
      if (raw) {
        return JSON.parse(raw);
      }
    } catch (e) {
      console.error('Error loading stored customized sidebar config:', e);
    }
    return DEFAULT_CUSTOMIZED_SIDEBAR_CONFIG;
  }

  private saveStoredConfig(config: CustomizedSidebarConfig): void {
    try {
      const storedUser = localStorage.getItem('user');
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const userId = userData?.id || userData?.userID || 'default';
      localStorage.setItem(this.getStorageKey(userId), JSON.stringify(config));
    } catch (e) {
      console.error('Error saving stored customized sidebar config:', e);
    }
  }

  private clearStoredConfig(): void {
    try {
      const storedUser = localStorage.getItem('user');
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const userId = userData?.id || userData?.userID || 'default';
      localStorage.removeItem(this.getStorageKey(userId));
    } catch (e) {
      console.error('Error clearing stored customized sidebar config:', e);
    }
  }
}
